import React, { useContext, useEffect, useState } from 'react';
import { FaArrowLeft } from 'react-icons/fa6';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { DiretrizVmpMedsus } from './DiretrizVmp';
import BarraInferiorBotoes from '../../../../../components/BarraInferiorBotoes';
import BarraSubAbas from '../../../../../components/BarraSubAbas';
import BoxConteudo from '../../../../../components/BoxConteudo';
import ContainerConteudo from '../../../../../components/ContainerConteudo';
import CustomCol from '../../../../../components/CustomCol';
import CustomInputDate from '../../../../../components/CustomInputs/CustomInputDate';
import CustomInputLink from '../../../../../components/CustomInputs/CustomInputLink';
import CustomInputText from '../../../../../components/CustomInputs/CustomInputText';
import CustomRow from '../../../../../components/CustomRow';
import CustomSelect from '../../../../../components/CustomSelects/CustomSelect';
import StyledLink from '../../../../../components/Link';
import Log from '../../../../../components/Log/Log';
import SubAbas from '../../../../../components/SubAbas';
import WorkspaceBotoes from '../../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../../components/WorkspaceFicha';
import { UserContext } from '../../../../../modules/contexts/Auth/UserProvider';
import { useColor } from '../../../../../modules/contexts/Color/useColor';
import { useSelect } from '../../../../../modules/contexts/Select/useSelect';
import {
  IMedsusDiretrizForm,
  IMedsusDiretrizRegistro,
} from '../../../../../modules/types/MedsusNew';
import { getMedsusDiretriz } from '../../../../../services/medsus/medsusDiretrizService';
import { functionLogData } from '../../../../../utils/functionLogData';
import { handleDelete } from '../../../../../utils/handleDelete';
import { handleSave } from '../../../../../utils/handleSave';
import { SubAbasMedusDaf } from '../../../../ses/Medsus/subabas';
import { defaultMedsusDiretrizFormMain } from '../../Produtos/produtosDadosGerais/utils/formMedsusDiretrizDefault';

export function MedsusDiretrizClinica() {
  //DADOS USUARIO
  const { cudProdutoDaf } = useContext(UserContext);
  const params = useParams();
  const fichaID = String(params.id);
  const theme = useTheme();
  const { getHexByNome } = useColor();

  //CONSTANTES DE REGISTRO PARA A VERSÃO 1.12.0 QUE É SOMENTE DAF
  const respRegistro = 130;
  const setorDaf = 1;

  // Enums
  const { getSelectDominioPai } = useSelect();
  const componentesAfOptions = getSelectDominioPai(1);
  const grupoFinanciamentoOptions = getSelectDominioPai(32);
  const tipoDiretrizOptions = getSelectDominioPai(105);
  const statusDiretrizOptions = getSelectDominioPai(111);
  const responsavelDiretrizOptions = getSelectDominioPai(114);
  const abrangenciaOptions = getSelectDominioPai(122);
  const responsavelRegistroOptions = getSelectDominioPai(129);

  // Gatilho para atualização do Log
  const [refreshTriggerLog, setRefreshTriggerLog] = useState(0);
  const triggerRefresh = React.useCallback(() => {
    setRefreshTriggerLog((prev) => prev + 1);
  }, []);

  const [logData, setLogData] = useState<IMedsusDiretrizRegistro | null>(null);

  // FORMULÁRIO
  const [dataRaw, setDataRaw] = useState<IMedsusDiretrizForm | null>(null);
  const [formMain, setFormMain] = useState<IMedsusDiretrizForm>(
    defaultMedsusDiretrizFormMain,
  );

  const fetchData = async (id: string | number): Promise<void> => {
    try {
      // 1. Buscar os dados
      const registroData = await getMedsusDiretriz(String(id));

      // 2. Atualiza dados auxiliares (não dependem de formValues)
      setLogData(registroData);

      // 4. Montagem do formulário (inalterada semanticamente)
      const formValues: IMedsusDiretrizForm = {
        id: registroData.id,

        sesId: 0,
        setorDafId: 0,

        ////////////////////////////////////////
        // Status e classificação
        status: registroData.status,
        tipo: registroData.tipo,
        respAquisicao: registroData.respAquisicao,
        respFinanciamento: registroData.respFinanciamento,
        grupoFinanciamento: registroData.grupoFinanciamento,
        componenteAF: registroData.componenteAF,
        abrangencia: registroData.abrangencia ?? 0,
        respRegistro: registroData.respRegistro ?? 0,

        ////////////////////////////////////////
        // Dados principais da Diretriz
        nome: registroData.nome ?? '',
        descricao: registroData.descricao ?? '',

        ////////////////////////////////////////
        // Links informacionais
        linkCidadao: registroData.linkCidadao ?? '',
        linkFarmaceutico: registroData.linkFarmaceutico ?? '',

        ////////////////////////////////////////
        // Datas
        dataIncorporacao: registroData.dataIncorporacao ?? '',
        dataDisponibilizacao: registroData.dataDisponibilizacao ?? '',
        dataExclusao: registroData.dataExclusao ?? '',

        ////////////////////////////////////////
        // Observações
        observacoes: registroData.observacoes ?? '',
      };

      // 5. Atualização final de estado (um único "bloco lógico")
      setFormMain(formValues);
      setDataRaw(formValues);
      triggerRefresh();
    } catch (error) {
      console.error('Erro ao obter dados:', error);
    }
  };

  useEffect(() => {
    if (fichaID !== '') {
      fetchData(Number(fichaID));
    }
  }, [fichaID]);

  const subAbas =
    fichaID !== 'undefined'
      ? SubAbasMedusDaf(fichaID)
      : [
          {
            label: 'Dados Gerais',
            rota: `/daf/produtosdaf/medsus/dadosgerais`,
          },
        ];

  const logs = functionLogData(logData);

  const handleChange = React.useCallback(
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const { name, value } = event.target;
      setFormMain((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const camposBaseObrigatorios: Record<string, string> = {
    status: 'Status da Diretriz',
    nome: 'Nome da Diretriz',
    respAquisicao: 'Responsável pela Aquisição',
    respFinanciamento: 'Responsável pelo Financiamento',
    tipo: 'Tipo da Diretriz',
    componenteAF: 'Componente AF',
    descricao: 'Descrição',
  };

  const camposBaseAjustar = {
    number: [
      'id',
      'incorporacaoId',
      'status',
      'tipo',
      'respRegistro',
      'sesId',
      'setorDafId',
      'componenteAF',
      'grupoFinanciamento',
      'respAquisicao',
      'respFinanciamento',
      'abrangencia',
    ],
    string: ['nome', 'descricao', 'linkCidadao', 'linkFarmaceutico'],
    date: ['dataDisponibilizacao', 'dataExclusao'],
  };

  const [camposObrigatorios, setCamposObrigatorios] = useState<
    Record<string, string>
  >(camposBaseObrigatorios);

  const [camposAjustar, setCamposAjustar] = useState(camposBaseAjustar);
  const [grupoFinanciamentoObrigatorio, setGrupoFinanciamentoObrigatorio] =
    useState(false);
  const [dataDisponibilizacaoObrigatorio, setDataDisponibilizacaoObrigatorio] =
    useState(false);
  const [dataExclusaoObrigatorio, setDataExclusaoObrigatorio] = useState(false);

  useEffect(() => {
    if (
      Number(formMain.componenteAF) === 3 ||
      Number(formMain.componenteAF) === 4
    ) {
      setGrupoFinanciamentoObrigatorio(true);

      // 1. Campos obrigatórios
      setCamposObrigatorios({
        ...camposBaseObrigatorios,
        grupoFinanciamento: 'Grupo Financiamento',
      });
    } else {
      setGrupoFinanciamentoObrigatorio(false);

      // 1. Remove campo obrigatório
      setCamposObrigatorios(camposBaseObrigatorios);

      setFormMain((prevState) => ({
        ...prevState,
        grupoFinanciamento: 0,
      }));
    }
  }, [formMain.componenteAF]);

  useEffect(() => {
    if (Number(formMain.status) === 112) {
      setDataDisponibilizacaoObrigatorio(true);

      // 1. Campos obrigatórios
      setCamposObrigatorios({
        ...camposBaseObrigatorios,
        dataDisponibilizacao: 'Grupo Financiamento',
      });
    } else if (Number(formMain.status) === 113) {
      setDataDisponibilizacaoObrigatorio(true);
      setDataExclusaoObrigatorio(true);

      // 1. Campos obrigatórios
      setCamposObrigatorios({
        ...camposBaseObrigatorios,
        dataDisponibilizacao: 'Data de Disponibilização',
        dataExclusao: 'Data de Exclusão',
      });
    } else {
      setDataDisponibilizacaoObrigatorio(false);
      setDataExclusaoObrigatorio(false);

      // 1. Remove campo obrigatório
      setCamposObrigatorios(camposBaseObrigatorios);

      setFormMain((prevState) => ({
        ...prevState,
        status: 0,
      }));
    }
  }, [formMain.status]);

  const salvar = React.useCallback(async () => {
    const formParaSalvar = {
      ...formMain,
      sesId: 0,
      setorDafId: setorDaf,
    };

    const formRaw = {
      ...dataRaw,
      sesId: 0,
      setorDafId: setorDaf,
    };

    setFormMain(formParaSalvar);

    const ok = await handleSave(
      formParaSalvar,
      camposObrigatorios,
      camposAjustar,
      'medsusdiretriz',
      'daf/produtosdaf/medsus',
      fetchData,
      true,
      formRaw,
    );

    triggerRefresh();
  }, [
    formMain,
    camposObrigatorios,
    camposAjustar,
    fetchData,
    dataRaw,
    triggerRefresh,
  ]);

  const deletar = () =>
    handleDelete(formMain.id, 'medsusdiretriz', 'daf/produtosdaf/medsus');

  const [statusExcluido, setStatusExcluido] = useState(true);

  useEffect(() => {
    if (Number(formMain.status) === 113) {
      setStatusExcluido(false);
    } else {
      setStatusExcluido(true);

      if (formMain.status !== 0) {
        setFormMain((prev) => ({
          ...prev,

          dataExclusao: '',
        }));
      }
    }
  }, [formMain.status, setFormMain]);

  const [bgStatus, setBgStatus] = useState<string>();
  useEffect(() => {
    if (Number(formMain.status) === 112) {
      setBgStatus(getHexByNome('Status BG - Verde', '#dddddd'));
    }

    if (Number(formMain.status) === 113) {
      setBgStatus(getHexByNome('Status BG - Vermelho', '#dddddd'));
    }
  }, [formMain.status, getHexByNome]);

  return (
    <>
      <SubAbas>
        <StyledLink
          to="/daf/produtosdaf/medsus"
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <FaArrowLeft />
        </StyledLink>
        <BarraSubAbas subAbas={subAbas} />
      </SubAbas>

      <ContainerConteudo>
        <WorkspaceFicha>
          {fichaID !== 'undefined' && (
            <Log
              {...logs}
              entity={'TB_MEDSUS_DIRETRIZ'}
              id={Number(formMain.id)}
              refreshTriggerLog={refreshTriggerLog}
            />
          )}

          {/* Identificação do Diretriz */}
          <BoxConteudo boxTitulo="Identificação da Diretriz">
            {/* Status */}
            <CustomRow style={{ marginTop: '-15px' }}>
              <CustomCol width="200px">
                <CustomSelect
                  name="status"
                  options={statusDiretrizOptions}
                  title="Status"
                  value={formMain.status}
                  onChange={handleChange}
                  style={{ backgroundColor: bgStatus }}
                  required
                />
              </CustomCol>
              <CustomCol width="215px">
                <CustomSelect
                  name="tipo"
                  options={tipoDiretrizOptions}
                  title="Tipo de Diretriz"
                  value={formMain.tipo}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol width="200px">
                <CustomInputDate
                  name="dataDisponibilizacao"
                  title="Data Disponibilização"
                  value={formMain.dataDisponibilizacao}
                  onChange={handleChange}
                  // readOnly={!dataDisponibilizacaoObrigatorio}
                  required={dataDisponibilizacaoObrigatorio}
                />
              </CustomCol>
              <CustomCol width="200px">
                <CustomInputDate
                  name="dataExclusao"
                  title="Data Exclusão"
                  value={formMain.dataExclusao}
                  onChange={handleChange}
                  readOnly={statusExcluido}
                  required={dataExclusaoObrigatorio}
                  // ref={dataExclusaoRef}
                  minDate={
                    formMain.dataDisponibilizacao
                      ? new Date(formMain.dataDisponibilizacao)
                      : undefined
                  }
                />
              </CustomCol>

              <CustomCol></CustomCol>
            </CustomRow>

            {/* Nome da Diretriz */}
            <CustomRow>
              <CustomCol>
                <CustomInputText
                  name="nome"
                  title="Nome da Diretriz"
                  value={formMain.nome}
                  onChange={handleChange}
                  style={
                    formMain.id !== 0
                      ? {
                          backgroundColor: theme.colors.azulgov,
                          fontWeight: 'bold',
                          color: 'white',
                        }
                      : {}
                  }
                  required
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <CustomCol width="140px">
                <CustomSelect
                  name="respRegistro"
                  options={responsavelRegistroOptions}
                  title="Resp. Registro"
                  value={
                    formMain.respRegistro === 0
                      ? respRegistro
                      : formMain.respRegistro
                  }
                  onChange={handleChange}
                  disabled
                  required
                />
              </CustomCol>
              <CustomCol>
                <CustomInputText
                  name="teste"
                  title="Setor DAF/Secretaria de Saúde"
                  value="Departamento de Assistência Farmacêutica e Insumos Estratégicos - DAF/SCTIE/MS"
                  onChange={handleChange}
                  readOnly
                />
              </CustomCol>
            </CustomRow>

            {/* Responsabilidades */}
            <CustomRow>
              <CustomCol width="200px">
                <CustomSelect
                  name="componenteAF"
                  options={componentesAfOptions}
                  title="Componente AF"
                  value={formMain.componenteAF}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol width="185px">
                <CustomSelect
                  name="grupoFinanciamento"
                  options={grupoFinanciamentoOptions}
                  title="Grupo Financiamento"
                  value={formMain.grupoFinanciamento}
                  onChange={handleChange}
                  required={grupoFinanciamentoObrigatorio}
                  disabled={!grupoFinanciamentoObrigatorio}
                />
              </CustomCol>
              <CustomCol width="300px">
                <CustomSelect
                  name="respAquisicao"
                  options={responsavelDiretrizOptions}
                  title="Resp. Aquisição"
                  value={formMain.respAquisicao}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol width="300px">
                <CustomSelect
                  name="respFinanciamento"
                  options={responsavelDiretrizOptions}
                  title="Resp. Financiamento"
                  value={formMain.respFinanciamento}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol>
                <CustomSelect
                  name="abrangencia"
                  options={abrangenciaOptions}
                  title="Abrangência"
                  value={formMain.abrangencia}
                  onChange={handleChange}
                  // required={handleGrupoFinanciamentoValidate()}
                  // disabled={!handleGrupoFinanciamentoValidate()}
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <CustomCol>
                <CustomInputText
                  name="descricao"
                  title="Descrição"
                  value={formMain.descricao}
                  type="textArea"
                  rows={5}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <CustomInputLink
                  name="linkCidadao"
                  title="Orientações para o Cidadão"
                  value={formMain.linkCidadao ?? ''}
                  onChange={handleChange}
                />
              </CustomCol>
              <CustomCol>
                <CustomInputLink
                  name="linkFarmaceutico"
                  title="Orientações para o Farmacêutico"
                  value={formMain.linkFarmaceutico ?? ''}
                  onChange={handleChange}
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <CustomInputText
                  name="observacoesDiretriz"
                  value={formMain.observacoes ?? ''}
                  title="Observações"
                  type="textArea"
                  onChange={handleChange}
                  rows={3}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>

          {fichaID !== 'undefined' &&
            DiretrizVmpMedsus({ diretrizID: fichaID })}
        </WorkspaceFicha>
      </ContainerConteudo>

      <BarraInferiorBotoes>
        {fichaID !== 'undefined' ? (
          <WorkspaceBotoes
            save
            delete={formMain.id ? true : false}
            onClickSave={salvar}
            onClickDelete={formMain.id ? deletar : undefined}
            saveDisabled={!cudProdutoDaf}
            deleteDisabled={!cudProdutoDaf}
          />
        ) : (
          <WorkspaceBotoes
            save
            onClickSave={salvar}
            saveDisabled={!cudProdutoDaf}
          />
        )}
      </BarraInferiorBotoes>
    </>
  );
}
